// src/pages/admin/DisputeManagementPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useUI } from '../../context/UIContext';
import { apiClient } from '../../api/apiClient';
import '../../styles/DisputeManagement.css';

interface Dispute {
  _id: string;
  commissionId: string;
  affiliateId: {
    _id: string;
    personalInfo: {
      fullName: string;
      email: string;
    };
    referralCode: string;
  };
  userId: {
    _id: string;
    name: string;
    email: string;
  };
  commissionAmount: number;
  bookingTotal: number;
  serviceInfo: {
    serviceName: string;
    stylistName: string;
    appointmentDate: string;
  };
  metadata: {
    disputedAt: string;
    disputeReason: string;
    disputeResolution?: string;
    disputeResolvedAt?: string;
  };
  createdAt: string;
}

const DisputeManagementPage: React.FC = () => {
  const { showSnackbar } = useUI();
  const [disputes, setDisputes] = useState<Dispute[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedDispute, setSelectedDispute] = useState<Dispute | null>(null);
  const [resolutionModal, setResolutionModal] = useState({
    isOpen: false,
    action: 'approve' as 'approve' | 'reject' | 'cancel',
    resolution: ''
  });

  const fetchDisputes = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiClient.get('/v1/admin/affiliates/disputes', {
        params: { page: currentPage, limit: 20 },
      });
      const data = response.data;

      if (data.success) {
        setDisputes(data.data.disputes);
        setCurrentPage(data.data.pagination.currentPage);
        setTotalPages(data.data.pagination.totalPages);
      }
    } catch (error: any) {
      console.error('Fetch disputes error:', error);
      showSnackbar(error?.message || 'An error occurred loading disputes', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, showSnackbar]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const openResolutionModal = (dispute: Dispute, action: 'approve' | 'reject' | 'cancel') => {
    setSelectedDispute(dispute);
    setResolutionModal({
      isOpen: true,
      action,
      resolution: ''
    });
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute || resolutionModal.resolution.trim().length < 10) {
      showSnackbar('Please provide a detailed resolution (minimum 10 characters)', 'error');
      return;
    }

    try {
      const response = await apiClient.post(
        `/v1/admin/affiliates/disputes/${selectedDispute._id}/resolve`,
        {
          action: resolutionModal.action,
          resolution: resolutionModal.resolution.trim(),
        },
      );
      const data = response.data;

      if (data.success) {
        showSnackbar(`Dispute ${resolutionModal.action === 'approve' ? 'approved' : resolutionModal.action === 'reject' ? 'rejected' : 'resolved'} successfully`, 'success');
        setResolutionModal({ isOpen: false, action: 'approve', resolution: '' });
        setSelectedDispute(null);
        fetchDisputes();
      }
    } catch (error: any) {
      console.error('Resolve dispute error:', error);
      showSnackbar(error?.message || 'An error occurred resolving the dispute', 'error');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div className="dispute-management">
      <div className="dispute-management__header">
        <div>
          <h1>Commission Disputes</h1>
          <p>Review and resolve referral agent commission disputes</p>
        </div>
        <div className="dispute-management__stats">
          <div className="stat-badge">
            <i className="fas fa-exclamation-triangle"></i>
            <span>{disputes.length} Open Disputes</span>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="dispute-management__loading">
          <div className="spinner"></div>
          <p>Loading disputes...</p>
        </div>
      ) : disputes.length === 0 ? (
        <div className="dispute-management__empty">
          <i className="fas fa-check-circle"></i>
          <p>No open disputes</p>
          <small>All disputes have been resolved</small>
        </div>
      ) : (
        <>
          <div className="dispute-management__table-container">
            <table className="dispute-management__table">
              <thead>
                <tr>
                  <th>Disputed Date</th>
                  <th>Commission ID</th>
                  <th>Referral Agent</th>
                  <th>Customer</th>
                  <th>Service</th>
                  <th>Amount</th>
                  <th>Dispute Reason</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {disputes.map((dispute) => (
                  <tr key={dispute._id}>
                    <td>{formatDate(dispute.metadata.disputedAt)}</td>
                    <td>
                      <code>{dispute.commissionId}</code>
                    </td>
                    <td>
                      <div>
                        <strong>{dispute.affiliateId.personalInfo.fullName}</strong>
                        <br />
                        <small>{dispute.affiliateId.personalInfo.email}</small>
                        <br />
                        <small>Code: {dispute.affiliateId.referralCode}</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{dispute.userId.name}</strong>
                        <br />
                        <small>{dispute.userId.email}</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{dispute.serviceInfo.serviceName}</strong>
                        <br />
                        <small>Stylist: {dispute.serviceInfo.stylistName}</small>
                        <br />
                        <small>{formatDate(dispute.serviceInfo.appointmentDate)}</small>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong>{formatCurrency(dispute.commissionAmount)}</strong>
                        <br />
                        <small>Booking: {formatCurrency(dispute.bookingTotal)}</small>
                      </div>
                    </td>
                    <td>
                      <div className="dispute-reason-box">
                        {dispute.metadata.disputeReason}
                      </div>
                    </td>
                    <td>
                      <div className="dispute-actions">
                        <button
                          onClick={() => openResolutionModal(dispute, 'approve')}
                          className="btn-action btn-action--success"
                          title="Approve dispute and commission"
                        >
                          <i className="fas fa-check"></i>
                          Approve
                        </button>
                        <button
                          onClick={() => openResolutionModal(dispute, 'reject')}
                          className="btn-action btn-action--warning"
                          title="Reject dispute"
                        >
                          <i className="fas fa-times"></i>
                          Reject
                        </button>
                        <button
                          onClick={() => openResolutionModal(dispute, 'cancel')}
                          className="btn-action btn-action--danger"
                          title="Cancel commission"
                        >
                          <i className="fas fa-ban"></i>
                          Cancel
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="dispute-management__pagination">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                <i className="fas fa-chevron-left"></i>
                Previous
              </button>
              <div className="pagination-info">
                Page {currentPage} of {totalPages}
              </div>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
                <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}

      {/* Resolution Modal */}
      {resolutionModal.isOpen && selectedDispute && (
        <div className="modal-overlay" onClick={() => setResolutionModal({ isOpen: false, action: 'approve', resolution: '' })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                {resolutionModal.action === 'approve' && '✅ Approve Dispute'}
                {resolutionModal.action === 'reject' && '❌ Reject Dispute'}
                {resolutionModal.action === 'cancel' && '⚠️ Cancel Commission'}
              </h2>
              <button
                className="modal-close"
                onClick={() => setResolutionModal({ isOpen: false, action: 'approve', resolution: '' })}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="dispute-summary">
                <h3>Commission Details</h3>
                <div className="summary-grid">
                  <div>
                    <label>Commission ID:</label>
                    <p>{selectedDispute.commissionId}</p>
                  </div>
                  <div>
                    <label>Amount:</label>
                    <p>{formatCurrency(selectedDispute.commissionAmount)}</p>
                  </div>
                  <div>
                    <label>Referral Agent:</label>
                    <p>{selectedDispute.affiliateId.personalInfo.fullName}</p>
                  </div>
                  <div>
                    <label>Service:</label>
                    <p>{selectedDispute.serviceInfo.serviceName}</p>
                  </div>
                </div>
              </div>

              <div className="dispute-reason-display">
                <h3>Dispute Reason</h3>
                <p>{selectedDispute.metadata.disputeReason}</p>
              </div>

              <div className="form-group">
                <label htmlFor="resolution">
                  Resolution Message *
                  {resolutionModal.action === 'approve' && ' (Explain why you\'re approving the dispute)'}
                  {resolutionModal.action === 'reject' && ' (Explain why you\'re rejecting the dispute)'}
                  {resolutionModal.action === 'cancel' && ' (Explain why you\'re cancelling the commission)'}
                </label>
                <textarea
                  id="resolution"
                  rows={5}
                  value={resolutionModal.resolution}
                  onChange={(e) => setResolutionModal({ ...resolutionModal, resolution: e.target.value })}
                  placeholder="Provide a detailed explanation of your decision..."
                  minLength={10}
                  required
                />
                <small>{resolutionModal.resolution.length}/10 characters (minimum 10 required)</small>
              </div>

              <div className="action-warning">
                {resolutionModal.action === 'approve' && (
                  <p className="warning-text">
                    <i className="fas fa-info-circle"></i>
                    This will approve the commission and make it available for payout.
                  </p>
                )}
                {resolutionModal.action === 'reject' && (
                  <p className="warning-text">
                    <i className="fas fa-info-circle"></i>
                    This will reject the dispute and restore the commission to its previous status.
                  </p>
                )}
                {resolutionModal.action === 'cancel' && (
                  <p className="warning-text warning-text--danger">
                    <i className="fas fa-exclamation-triangle"></i>
                    This will cancel the commission permanently. This action cannot be undone.
                  </p>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-secondary"
                onClick={() => setResolutionModal({ isOpen: false, action: 'approve', resolution: '' })}
              >
                Cancel
              </button>
              <button
                className={`btn-primary ${resolutionModal.action === 'cancel' ? 'btn-primary--danger' : ''}`}
                onClick={handleResolveDispute}
                disabled={resolutionModal.resolution.trim().length < 10}
              >
                {resolutionModal.action === 'approve' && 'Approve Dispute'}
                {resolutionModal.action === 'reject' && 'Reject Dispute'}
                {resolutionModal.action === 'cancel' && 'Cancel Commission'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisputeManagementPage;
